import React, { useState, useEffect } from 'react';
import { X, Check, Minus, Crown, Zap, Target, Brain, Shield, TrendingUp, Sparkles, Newspaper, PenTool, Lightbulb, RefreshCw, BarChart3, Layers, GraduationCap, FileText, Calendar, Clock, AlertTriangle, Users, Library, BookOpen } from 'lucide-react';

export default function WhyChooseCLARIASPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // ── Click-only popup (Feb 14 2026).
    // Previously auto-opened on scroll+time gate. Now disabled entirely —
    // the landing page already exposes the comparison via the 4-moats
    // section, the 5-hub "Everything You Need" grid, and the navbar
    // "Why Choose Clarias" link. Auto-modals were interrupting the
    // editorial flow + killing first impressions. Manual triggers still
    // work via the `open-clarias-comparison` custom event.

    const handleOpen = () => {
      setIsClosing(false);
      setIsVisible(true);
    };
    window.addEventListener('open-clarias-comparison', handleOpen);

    return () => {
      window.removeEventListener('open-clarias-comparison', handleOpen);
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    sessionStorage.setItem('clarias-popup-shown', 'true');
    
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  // EXACT SAME DATA FROM ComparisonTable.js
  const features = [
    // PRELIMS PREPARATION
    {
      category: "Prelims Preparation",
      items: [
        {
          feature: "Master Material (154+ Books)",
          icon: Library,
          coaching: { status: "partial", text: "Scattered, ₹15K+ books" },
          chatgpt: { status: "no", text: "No curated material" },
          us: { status: "yes", text: "154+ books mapped + chapter-tagged" }
        },
        {
          feature: "Chapter & Sub-topic Practice",
          icon: BookOpen,
          coaching: { status: "partial", text: "Generic chapter tests" },
          chatgpt: { status: "no", text: "No syllabus structure" },
          us: { status: "yes", text: "Drill into sub-topics (e.g. Boats & Streams)" }
        },
        {
          feature: "Daily Current Affairs",
          icon: Newspaper,
          coaching: { status: "partial", text: "Monthly compilations, delayed" },
          chatgpt: { status: "no", text: "No daily curation" },
          us: { status: "yes", text: "Daily brief · PIB + The Hindu · GS-tagged" }
        },
        {
          feature: "MCQ Practice with Analytics",
          icon: Target,
          coaching: { status: "partial", text: "Fixed schedule, basic" },
          chatgpt: { status: "no", text: "No practice mode" },
          us: { status: "yes", text: "25,000+ MCQs + panic/speed analysis" }
        },
        {
          feature: "PYQ Papers (2010-2024)",
          icon: Layers,
          coaching: { status: "yes", text: "Available in books" },
          chatgpt: { status: "no", text: "No organized PYQs" },
          us: { status: "yes", text: "15 years + instant rubric explanations" }
        },
        {
          feature: "PYQ Heatmap & Analyser",
          icon: BarChart3,
          coaching: { status: "no", text: "Not available" },
          chatgpt: { status: "no", text: "Cannot analyze patterns" },
          us: { status: "yes", text: "Topic heatmaps + frequency trends" }
        },
        {
          feature: "CA to MCQ Converter",
          icon: RefreshCw,
          coaching: { status: "no", text: "Not available" },
          chatgpt: { status: "partial", text: "Generic questions" },
          us: { status: "yes", text: "UPSC-pattern MCQs from news" }
        },
        {
          feature: "Newspaper to MCQ",
          icon: Newspaper,
          coaching: { status: "no", text: "Not available" },
          chatgpt: { status: "no", text: "No news integration" },
          us: { status: "yes", text: "Auto-generated from daily news" }
        },
        {
          feature: "CSAT Practice Suite",
          icon: Shield,
          coaching: { status: "partial", text: "Basic practice only" },
          chatgpt: { status: "partial", text: "Generic aptitude" },
          us: { status: "yes", text: "Dedicated suite + safety score" }
        }
      ]
    },
    // MAINS PREPARATION
    {
      category: "Mains Preparation",
      items: [
        {
          feature: "GS Answer Writing (GS1-GS4)",
          icon: PenTool,
          coaching: { status: "partial", text: "Manual feedback, slow" },
          chatgpt: { status: "partial", text: "Generic, no structure" },
          us: { status: "yes", text: "Instant, diagram-ready answers" }
        },
        {
          feature: "Essay Intelligence Suite",
          icon: FileText,
          coaching: { status: "partial", text: "No prediction system" },
          chatgpt: { status: "partial", text: "Generic essays" },
          us: { status: "yes", text: "Predicted band + /125 rubric scoring" }
        },
        {
          feature: "Predicted Exam Band",
          icon: TrendingUp,
          coaching: { status: "no", text: "No live band forecast" },
          chatgpt: { status: "no", text: "Cannot predict UPSC bands" },
          us: { status: "yes", text: "Live exam-band forecast on every answer" }
        },
        {
          feature: "Word Count & Balance Check",
          icon: FileText,
          coaching: { status: "partial", text: "Manual, no diagnostics" },
          chatgpt: { status: "no", text: "No UPSC norms" },
          us: { status: "yes", text: "Auto count + intro/body/conclusion balance" }
        },
        {
          feature: "Instant Mains Evaluation",
          icon: BarChart3,
          coaching: { status: "partial", text: "₹50-100/answer, 3-7 days" },
          chatgpt: { status: "no", text: "No UPSC rubric" },
          us: { status: "yes", text: "Instant score + improvement plan" }
        },
        {
          feature: "Expected Questions",
          icon: Lightbulb,
          coaching: { status: "partial", text: "Generic guesses" },
          chatgpt: { status: "no", text: "No prediction" },
          us: { status: "yes", text: "Pattern-based per paper" }
        },
        {
          feature: "Handwritten Answer Evaluation",
          icon: PenTool,
          coaching: { status: "partial", text: "Manual, expensive" },
          chatgpt: { status: "no", text: "Cannot read images" },
          us: { status: "yes", text: "Multi-page upload + rubric scoring" }
        },
        {
          feature: "GS Mains Topic Mastery Radar",
          icon: BarChart3,
          coaching: { status: "no", text: "No topic-wise tracking" },
          chatgpt: { status: "no", text: "No memory across answers" },
          us: { status: "yes", text: "Radar map across GS1–GS4 topics" }
        }
      ]
    },
    // CURRENT AFFAIRS & INTELLIGENCE
    {
      category: "Current Affairs & Intelligence",
      items: [
        {
          feature: "Daily Intelligence",
          icon: Newspaper,
          coaching: { status: "partial", text: "Monthly magazines, delayed" },
          chatgpt: { status: "no", text: "No curation" },
          us: { status: "yes", text: "Daily + priority scoring" }
        },
        {
          feature: "CA → GS Mapping",
          icon: Target,
          coaching: { status: "partial", text: "Generic tagging" },
          chatgpt: { status: "no", text: "No syllabus awareness" },
          us: { status: "yes", text: "Auto GS1-4 + Prelims mapping" }
        },
        {
          feature: "Weekly/Monthly Digests",
          icon: Calendar,
          coaching: { status: "no", text: "No personalized digests" },
          chatgpt: { status: "no", text: "No memory" },
          us: { status: "yes", text: "Personalized intelligence reports" }
        },
        {
          feature: "Editorial Analyzer",
          icon: FileText,
          coaching: { status: "no", text: "Not available" },
          chatgpt: { status: "partial", text: "Generic summary only" },
          us: { status: "yes", text: "Deep UPSC analysis + MCQs" }
        }
      ]
    },
    // DECISION-TRAINED LEARNING
    {
      category: "Decision-Trained Learning",
      items: [
        {
          feature: "Concept Intelligence",
          icon: Brain,
          coaching: { status: "no", text: "Fixed content only" },
          chatgpt: { status: "partial", text: "Generic, no syllabus depth" },
          us: { status: "yes", text: "Syllabus-mapped + GS relevance" }
        },
        {
          feature: "24×7 Doubt Resolution",
          icon: Lightbulb,
          coaching: { status: "partial", text: "Limited to batch hours" },
          chatgpt: { status: "partial", text: "No exam context" },
          us: { status: "yes", text: "Mains-depth + PYQ links" }
        },
        {
          feature: "PYQ Pattern Analysis",
          icon: TrendingUp,
          coaching: { status: "partial", text: "Manual, outdated" },
          chatgpt: { status: "no", text: "No UPSC memory" },
          us: { status: "yes", text: "Trend-based topic predictions" }
        }
      ]
    },
    // WHAT MAKES US DIFFERENT
    {
      category: "What Makes Us Different",
      highlight: true,
      items: [
        {
          feature: "Topper-Style Answer Writing",
          icon: PenTool,
          coaching: { status: "no", text: "Only model answers in books" },
          chatgpt: { status: "no", text: "Generic AI, not paper-specific" },
          us: { status: "yes", text: "4 paper-specific engines · GS1–GS4 + Essay" }
        },
        {
          feature: "Prelims Clearance Prediction",
          icon: Target,
          coaching: { status: "no", text: "No prediction system" },
          chatgpt: { status: "no", text: "Cannot predict" },
          us: { status: "yes", text: "Live safety zone tracking" }
        },
        {
          feature: "Daily Command System",
          icon: Zap,
          coaching: { status: "no", text: "Same for everyone" },
          chatgpt: { status: "no", text: "No personalization" },
          us: { status: "yes", text: "Personalised guidance daily" }
        },
        {
          feature: "Exam Behavior Analysis",
          icon: Brain,
          coaching: { status: "no", text: "No behavior feedback" },
          chatgpt: { status: "no", text: "No tracking" },
          us: { status: "yes", text: "Panic index + discipline score" }
        },
        {
          feature: "Weak Area Detection",
          icon: AlertTriangle,
          coaching: { status: "partial", text: "Basic test results" },
          chatgpt: { status: "no", text: "No analysis" },
          us: { status: "yes", text: "Auto-detected focus areas" }
        },
        {
          feature: "Trap Question Detector",
          icon: Shield,
          coaching: { status: "no", text: "Not available" },
          chatgpt: { status: "no", text: "Cannot detect" },
          us: { status: "yes", text: "Pattern-based alerts" }
        },
        {
          feature: "What-to-Skip Guidance",
          icon: Clock,
          coaching: { status: "no", text: "Focus on more, not less" },
          chatgpt: { status: "no", text: "No skip guidance" },
          us: { status: "yes", text: "Clear stop-signals" }
        },
        {
          feature: "Continuous Progress Memory",
          icon: Sparkles,
          coaching: { status: "no", text: "No personalization" },
          chatgpt: { status: "no", text: "Forgets every session" },
          us: { status: "yes", text: "Remembers your journey" }
        },
        {
          feature: "Subject-wise Analytics",
          icon: BarChart3,
          coaching: { status: "partial", text: "Basic test scores" },
          chatgpt: { status: "no", text: "No tracking" },
          us: { status: "yes", text: "Trends + peer comparison" }
        },
        {
          feature: "CSAT Sub-topic Intelligence",
          icon: Brain,
          coaching: { status: "no", text: "Aggregate scores only" },
          chatgpt: { status: "no", text: "No tracking" },
          us: { status: "yes", text: "Boats, time-work, RC — sub-topic diagnostics" }
        },
        {
          feature: "Parent Portal",
          icon: Users,
          coaching: { status: "no", text: "No parent access" },
          chatgpt: { status: "no", text: "Not available" },
          us: { status: "yes", text: "Real-time progress monitoring" }
        }
      ]
    }
  ];

  const totalFeatures = features.reduce((acc, section) => acc + section.items.length, 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case "yes":
        return (
          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </div>
        );
      case "no":
        return (
          <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-red-400" strokeWidth={2} />
          </div>
        );
      case "partial":
        return (
          <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Minus className="w-3.5 h-3.5 text-amber-400" strokeWidth={2} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
      onClick={handleBackdropClick}
    >
      <div 
        className={`relative bg-slate-900/95 backdrop-blur-xl rounded-2xl max-w-6xl w-full max-h-[80vh] overflow-hidden border border-slate-700/50 shadow-2xl transform transition-all duration-300 flex flex-col ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white transition-all z-20"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-4 md:p-6 text-center border-b border-slate-700/50 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 shrink-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-300" />
            <span className="text-xs font-medium text-emerald-300">Why Choose CLARIAS?</span>
          </div>
          <h2 className="text-lg md:text-2xl font-bold text-white leading-tight">
            Coaching gives content. Generic chatbots give answers.
            <span className="text-emerald-400 block md:inline"> We give clearance confidence.</span>
          </h2>
          <p className="text-sm text-slate-400 mt-2">{totalFeatures} features designed to eliminate wasted preparation.</p>
        </div>

        {/* Table Container — scrollable */}
        <div className="overflow-y-auto flex-1" style={{ minHeight: 0 }}>

        {/* Quick Moats Strip — INSIDE scrollable area, scrolls away with content */}
        <div className="px-5 md:px-8 pb-4 pt-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">
              The 4 Things Only CLARIAS Does
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: PenTool,   title: 'Topper-Style Answer Writing', sub: '4 engines · GS1–GS4 + Essay',     color: 'amber' },
              { icon: Brain,     title: 'Decision Intelligence',        sub: 'Marks lost · Weak areas · Mistake patterns',    color: 'sky' },
              { icon: RefreshCw, title: 'Daily CA → Sub-topic Practice', sub: 'Auto-injected · 30+ live Indian sources',     color: 'emerald' },
              { icon: Target,    title: 'Examiner-Grade Rubric',        sub: '7-dimension Mains grading',       color: 'rose' },
            ].map((m, i) => {
              const Ic = m.icon;
              const colorMap = {
                amber:   { bg: 'bg-amber-500/10',   br: 'border-amber-500/30',   tx: 'text-amber-300',   ic: 'text-amber-400' },
                sky:     { bg: 'bg-sky-500/10',     br: 'border-sky-500/30',     tx: 'text-sky-300',     ic: 'text-sky-400' },
                emerald: { bg: 'bg-emerald-500/10', br: 'border-emerald-500/30', tx: 'text-emerald-300', ic: 'text-emerald-400' },
                rose:    { bg: 'bg-rose-500/10',    br: 'border-rose-500/30',    tx: 'text-rose-300',    ic: 'text-rose-400' },
              }[m.color];
              return (
                <div
                  key={i}
                  className={`rounded-xl ${colorMap.bg} border ${colorMap.br} px-3 py-3 hover:scale-[1.02] transition-transform flex items-start gap-2.5`}
                  data-testid={`quick-moat-${i}`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-white/5 border ${colorMap.br} flex items-center justify-center flex-shrink-0`}>
                    <Ic className={`w-4 h-4 ${colorMap.ic}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-white leading-tight mb-1">{m.title}</p>
                    <p className={`text-[11px] ${colorMap.tx} leading-tight`}>{m.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
          {/* Table Header - Sticky */}
          <div className="grid grid-cols-4 border-b border-slate-700/50 bg-slate-800/90 sticky top-0 z-10">
            <div className="p-3 md:p-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Features</p>
            </div>
            <div className="p-3 md:p-4 text-center border-l border-slate-700/50">
              <p className="text-sm font-semibold text-slate-300">Coaching</p>
              <p className="text-xs text-slate-500">₹1-2 Lakh/year</p>
            </div>
            <div className="p-3 md:p-4 text-center border-l border-slate-700/50">
              <p className="text-sm font-semibold text-slate-300">Generic Chatbots</p>
              <p className="text-xs text-slate-500">No UPSC training</p>
            </div>
            <div className="p-3 md:p-4 text-center border-l border-emerald-500/30 bg-gradient-to-b from-emerald-500/20 to-emerald-500/10">
              <div className="flex items-center justify-center gap-1">
                <Crown className="w-4 h-4 text-amber-400" />
                <p className="text-sm font-bold text-white">CLARIAS</p>
              </div>
            </div>
          </div>

          {/* Table Body */}
          {features.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              {/* Category Header */}
              <div className={`px-4 py-2 border-b border-slate-700/30 ${
                section.highlight 
                  ? 'bg-gradient-to-r from-emerald-600/20 via-teal-600/10 to-emerald-600/20' 
                  : 'bg-slate-800/40'
              }`}>
                <div className="flex items-center gap-2">
                  {section.highlight && <Sparkles className="w-4 h-4 text-emerald-300" />}
                  <p className={`text-xs font-bold uppercase tracking-wider ${
                    section.highlight ? 'text-emerald-200' : 'text-slate-300'
                  }`}>
                    {section.category}
                  </p>
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-slate-600/50 text-slate-300 rounded-full">
                    {section.items.length}
                  </span>
                  {section.highlight && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full">
                      Our Edge
                    </span>
                  )}
                </div>
              </div>

              {/* Category Items */}
              {section.items.map((item, itemIdx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={itemIdx}
                    className={`grid grid-cols-4 border-b border-slate-700/30 hover:bg-slate-800/30 ${
                      section.highlight ? 'bg-gradient-to-r from-emerald-500/5 via-transparent to-transparent' : ''
                    }`}
                  >
                    {/* Feature Name */}
                    <div className="p-2 md:p-3 flex items-start gap-2">
                      {Icon && (
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                          section.highlight 
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                            : 'bg-slate-700'
                        }`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      <div>
                        <p className={`font-medium text-xs md:text-sm ${section.highlight ? 'text-white' : 'text-slate-200'}`}>
                          {item.feature}
                        </p>
                      </div>
                    </div>

                    {/* Coaching */}
                    <div className="p-2 md:p-3 border-l border-slate-700/30 flex flex-col items-center justify-center text-center">
                      {getStatusIcon(item.coaching.status)}
                      <p className="text-xs text-slate-400 mt-1 hidden md:block">{item.coaching.text}</p>
                    </div>

                    {/* ChatGPT */}
                    <div className="p-2 md:p-3 border-l border-slate-700/30 flex flex-col items-center justify-center text-center">
                      {getStatusIcon(item.chatgpt.status)}
                      <p className="text-xs text-slate-400 mt-1 hidden md:block">{item.chatgpt.text}</p>
                    </div>

                    {/* CLARIAS */}
                    <div className="p-2 md:p-3 border-l border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 to-teal-500/5 flex flex-col items-center justify-center text-center">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </div>
                      <p className="text-xs text-emerald-300 font-medium mt-1 hidden md:block">{item.us.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Footer CTA - Inside Scrollable Area */}
          <div className="p-3 md:p-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 border-t border-emerald-500/30">
            <div className="text-center">
              <p className="text-white/90 text-sm mb-2">
                {totalFeatures} features for{' '}
                <span className="text-white/60 line-through mr-1">₹23,999</span>
                <span className="font-bold">₹6,999</span> — Founding pricing
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-white text-emerald-600 font-bold rounded-full hover:bg-slate-100 transition-all shadow-xl flex items-center gap-2 mx-auto"
              >
                <Sparkles className="w-4 h-4" />
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
